/**
 * Write a broadcast — the message, the audience, and who that audience turns
 * out to be (FR-E9). A kit `Dialog` laid out as the Branches dialog is (C14
 * §10.6 item 7). The draft lives on the page (`Compose`), so closing this to
 * confirm the send, and coming back from *Not yet*, loses nothing.
 *
 * **The preview is the first of two locks.** A broadcast cannot be recalled,
 * so *Send* stays disabled until the audience has been resolved and shown — by
 * name, not only by count, because "240 clients" is a number nobody can check
 * and a list is. It also says, before sending, how many have **no address on
 * file**, so a firm fixes that now rather than reading it in a report
 * afterwards. The second lock is the page's `ConfirmDialog`.
 */

import { Button, Dialog, Input, Select, Textarea } from '@fiveox/ui'
import { Megaphone, Users } from 'lucide-react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { type AudienceFilters, type AudiencePreview, audienceSummary } from '../../lib/api/chat'
import type { Service } from '../../lib/api/services'

export interface BroadcastDraft {
  subject: string
  body: string
  audience: AudienceFilters
  templateCode: string
}

export const EMPTY_DRAFT: BroadcastDraft = { subject: '', body: '', audience: {}, templateCode: '' }

export function ComposeDialog({
  open,
  draft,
  onDraft,
  preview,
  catalog,
  resolving,
  error,
  ready,
  onResolve,
  onSend,
  onClose,
}: {
  open: boolean
  draft: BroadcastDraft
  onDraft: (next: BroadcastDraft) => void
  preview: AudiencePreview | null
  catalog: Service[]
  resolving: boolean
  error: string | null
  /** Subject, message and a resolved audience of at least one. */
  ready: boolean
  onResolve: () => void
  /** Hands over to the page's confirmation. */
  onSend: () => void
  onClose: () => void
}) {
  const setAudience = (audience: AudienceFilters) => onDraft({ ...draft, audience })
  const why = !draft.subject.trim()
    ? 'Give it a subject first.'
    : !draft.body.trim()
      ? 'Write the message first.'
      : !preview
        ? 'Check who will get this first — a broadcast cannot be recalled.'
        : preview.count === 0
          ? 'Nobody matches this audience.'
          : undefined

  return (
    <Dialog open={open} onClose={onClose} title="Write a broadcast" error={error} width="xl">
      <div className="space-y-5">
        <div className={FORM_GRID}>
          <FormRow label="Subject" htmlFor="broadcast-subject">
            <Input
              id="broadcast-subject"
              value={draft.subject}
              maxLength={200}
              onChange={(event) => onDraft({ ...draft, subject: event.target.value })}
              placeholder="GST returns are due on the 20th"
            />
          </FormRow>
          <FormRow label="Message" htmlFor="broadcast-body">
            <Textarea
              id="broadcast-body"
              value={draft.body}
              rows={5}
              onChange={(event) => onDraft({ ...draft, body: event.target.value })}
              placeholder="Please send your sales and purchase registers by the 15th."
            />
          </FormRow>
        </div>

        <section className="border-border space-y-4 border-t pt-4" aria-labelledby="broadcast-audience">
          <h3 id="broadcast-audience" className="text-foreground text-sm font-semibold">
            Who gets it
          </h3>
          <div className={FORM_GRID}>
            <FormRow label="Service" htmlFor="broadcast-service">
              <Select
                id="broadcast-service"
                value={draft.audience.service_id ?? ''}
                onChange={(event) =>
                  setAudience({ ...draft.audience, service_id: event.target.value || undefined })
                }
              >
                <option value="">Any service</option>
                {catalog.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </Select>
            </FormRow>
            <FormRow label="Kind of client" htmlFor="broadcast-kind">
              <Select
                id="broadcast-kind"
                value={draft.audience.client_kind ?? ''}
                onChange={(event) =>
                  setAudience({ ...draft.audience, client_kind: event.target.value || undefined })
                }
              >
                <option value="">Any client</option>
                <option value="individual">Individuals</option>
                <option value="business">Businesses</option>
              </Select>
            </FormRow>
            <FormRow label="Tag" htmlFor="broadcast-tag">
              <Input
                id="broadcast-tag"
                value={draft.audience.tag ?? ''}
                onChange={(event) =>
                  setAudience({ ...draft.audience, tag: event.target.value || undefined })
                }
                placeholder="Any tag"
              />
            </FormRow>
            <FormRow label="Email template" htmlFor="broadcast-template">
              <Input
                id="broadcast-template"
                value={draft.templateCode}
                onChange={(event) => onDraft({ ...draft, templateCode: event.target.value })}
                placeholder="Leave blank to post in their portal only"
                hint="A core-communication template code. With one, clients with an address also get it by email."
              />
            </FormRow>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={onResolve} loading={resolving}>
              <Users size={16} aria-hidden /> Who will get this?
            </Button>
            {!preview && (
              <span className="text-muted-foreground text-xs">
                Check before you send — a broadcast cannot be recalled.
              </span>
            )}
          </div>

          {preview && (
            <div className="border-border bg-muted/40 rounded-xl border p-3" data-audience="">
              <p className="text-foreground text-sm font-medium">
                {audienceSummary(preview, Boolean(draft.templateCode))}
              </p>
              {preview.recipients.length > 0 && (
                <ul className="text-muted-foreground mt-2 space-y-1 text-xs">
                  {preview.recipients.slice(0, 25).map((row) => (
                    <li key={row.client_id}>
                      <span className="text-foreground">{row.name}</span>
                      {row.case_number ? ` · ${row.case_number}` : ''}
                      {row.has_address ? '' : ' · no address on file'}
                    </li>
                  ))}
                </ul>
              )}
              {preview.truncated && <p className="text-muted-foreground mt-1 text-xs">…and more.</p>}
            </div>
          )}
        </section>

        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {/* The gate. Not a nicety — a broadcast cannot be recalled. */}
          <span title={why}>
            <Button onClick={onSend} disabled={!ready}>
              <Megaphone size={16} aria-hidden /> Send…
            </Button>
          </span>
        </DialogActions>
      </div>
    </Dialog>
  )
}
